import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Zap, Users, PlayCircle, PauseCircle, ArrowRight, Filter } from "lucide-react";
import AdminLayout from "@/components/app/AdminLayout";
import EmailEngagementStats from "@/components/app/EmailEngagementStats";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const TRIGGER_LABELS = {
  manual: "ידני",
  on_signup: "בעת הרשמה",
  on_tag_added: "בעת הוספת תיוג",
};

const AUDIENCE_SHORT = {
  all: "כל המנויים",
  tags_or: "OR-תיוגים",
  tags_and: "AND-תיוגים",
};

export default function AdminDrips() {
  const [drips, setDrips] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/admin/drips"),
        api.get("/admin/email/stats/summary"),
      ]);
      setDrips(d.data);
      setStats((s.data && s.data.by_drip) || {});
    } catch (err) {
      console.error("failed drips", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <AdminLayout title="Drip Campaigns">
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-stone-500">
          סדרות מייל אוטומטיות המבוססות על תיוגים. לכל דריפ שלבים עם מרווח זמן. Anti-collision (10 דק&apos;) מבטיח שלא נשלחים שני מיילים באותה חלון.
        </p>
        <Link to="/admin/drips/new" data-testid="new-drip-btn">
          <Button className="h-11 rounded-full bg-brand-charcoal hover:bg-stone-800 text-white">
            <Plus className="w-4 h-4 me-2" />Drip חדש
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-500">טוען...</div>
      ) : drips.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="drips-grid">
          {drips.map((d) => (
            <DripCard key={d.id} drip={d} engagement={stats[d.id]} />
          ))}
        </div>
      )}
    </AdminLayout>
  );
}

function DripCard({ drip, engagement }) {
  const stats = drip.stats || { active: 0, completed: 0, total: 0 };
  const stepsCount = (drip.steps || []).length;
  return (
    <Link
      to={`/admin/drips/${drip.id}`}
      data-testid={`drip-card-${drip.id}`}
      className={`block bg-white rounded-2xl p-5 border ${drip.is_active ? "border-stone-200" : "border-stone-200 opacity-60"} hover:shadow-md hover:-translate-y-0.5 transition-all`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Zap className={`w-5 h-5 ${drip.is_active ? "text-brand-amber" : "text-stone-400"}`} />
          <h3 className="font-[800] text-base">{drip.name}</h3>
        </div>
        {drip.is_active ? (
          <PlayCircle className="w-5 h-5 text-emerald-500" />
        ) : (
          <PauseCircle className="w-5 h-5 text-stone-400" />
        )}
      </div>

      {drip.description && (
        <p className="text-sm text-stone-500 mb-3 line-clamp-2">{drip.description}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">
          <Filter className="w-3 h-3" />{AUDIENCE_SHORT[drip.audience_type] || drip.audience_type}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
          {TRIGGER_LABELS[drip.entry_trigger] || drip.entry_trigger}
          {drip.entry_tag && ` · ${drip.entry_tag}`}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-50 text-brand-amber border border-amber-200">
          {stepsCount} שלבים
        </span>
      </div>

      {/* Email engagement — sends/opens/clicks derived from email_sends */}
      <div className="mb-3">
        <EmailEngagementStats stats={engagement} testId={`drip-stats-${drip.id}`} />
      </div>

      <div className="flex items-center justify-between border-t border-stone-100 pt-3 mt-3">
        <div className="flex gap-4 text-xs">
          <Stat label="פעילים" value={stats.active} color="emerald" />
          <Stat label="הושלמו" value={stats.completed} color="blue" />
          <Stat label="סה״כ" value={stats.total} />
        </div>
        <ArrowRight className="w-4 h-4 text-stone-400" />
      </div>
    </Link>
  );
}

function Stat({ label, value, color }) {
  const colors = {
    emerald: "text-emerald-700",
    blue: "text-blue-700",
    default: "text-stone-700",
  };
  return (
    <div className="text-center">
      <div className={`font-[800] ${colors[color] || colors.default}`}>{value}</div>
      <div className="text-[10px] text-stone-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-2xl p-12 border border-dashed border-stone-300 text-center" data-testid="drips-empty">
      <Zap className="w-12 h-12 text-stone-300 mx-auto mb-4" />
      <h3 className="font-[800] mb-2">עדיין אין Drip Campaigns</h3>
      <p className="text-sm text-stone-500 mb-6 max-w-md mx-auto">
        Drip הוא סדרת מיילים אוטומטית, שהופעלת ידנית או ע&quot;י תיוג ליד. לדוגמה: &quot;אחרי שהליד תויג כ-hot_lead — שלח 3 מיילים במרווח יומיים&quot;.
      </p>
      <Link to="/admin/drips/new">
        <Button className="h-11 rounded-full bg-brand-charcoal text-white">
          <Plus className="w-4 h-4 me-2" />צור Drip ראשון
        </Button>
      </Link>
    </div>
  );
}
